import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, Loader2 } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { formatVnDate, formatVnDateTime } from "@apv-erp/status";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface ResequenceByDeadlineDialogProps {
  machine: { id: string; name?: string; code?: string } | null;
  onClose: () => void;
  onApplied?: () => void;
}

/**
 * "Xếp lại theo hạn" một máy: xem trước thứ tự mới (công đoạn chưa chạy, chưa khóa lịch xếp theo hạn giao),
 * người điều độ kiểm tra rồi mới [Lưu]. Công đoạn đang chạy / đã ban hành / khóa lịch giữ nguyên.
 */
export function ResequenceByDeadlineDialog({ machine, onClose, onApplied }: ResequenceByDeadlineDialogProps) {
  const [saving, setSaving] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["resequence-by-deadline-preview", machine?.id],
    queryFn: () => trpcClient.productionScheduling.previewResequenceByDeadline.query({ machineId: machine!.id }),
    enabled: !!machine?.id,
    staleTime: 0,
  });
  const items: any[] = (data as any)?.items || [];

  const handleApply = async () => {
    if (!machine) return;
    setSaving(true);
    try {
      const res: any = await trpcClient.productionScheduling.resequenceByDeadline.mutate({ machineId: machine.id });
      toast.success(`Đã xếp lại ${res.items.length} công đoạn theo hạn giao (trễ hạn: ${res.lateBefore} → ${res.lateAfter}).`);
      onApplied?.();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || "Xếp lại theo hạn thất bại");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!machine} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-indigo-600" />
            Xếp lại theo hạn — {machine?.name || machine?.code}
          </DialogTitle>
          <DialogDescription>
            Xếp theo hạn giao sớm trước; công đoạn đang chạy, đã ban hành, đã khóa lịch giữ nguyên.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Đang tính thứ tự mới...
          </div>
        ) : error ? (
          <div className="text-sm text-rose-600 py-4">{(error as any)?.message || "Không tính được thứ tự mới"}</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 py-4" data-testid="resequence-empty">
            Máy không có công đoạn chưa chạy nào để xếp lại.
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-600 flex flex-wrap gap-3" data-testid="resequence-summary">
              <span>{items.length} công đoạn xếp lại</span>
              {(data as any)?.fixedCount > 0 && <span>{(data as any).fixedCount} công đoạn giữ nguyên</span>}
              <span className={cn("font-semibold", (data as any).lateAfter > 0 ? "text-rose-600" : "text-emerald-600")}>
                Trễ hạn: {(data as any).lateBefore} → {(data as any).lateAfter}
              </span>
            </div>
            <div className="max-h-[55vh] overflow-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-1.5 text-left">#</th>
                    <th className="px-2 py-1.5 text-left">Lệnh / Công đoạn</th>
                    <th className="px-2 py-1.5 text-left">Hạn giao</th>
                    <th className="px-2 py-1.5 text-left">Lịch cũ</th>
                    <th className="px-2 py-1.5 text-left">Lịch mới</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={it.stepId} className="border-t" data-testid="resequence-row">
                      <td className="px-2 py-1.5 font-mono">{idx + 1}</td>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold font-mono">{it.lsx}</div>
                        <div className="text-slate-500 break-words whitespace-normal max-w-[260px]">
                          {it.stepName}
                          {it.productName ? ` · ${it.productName}` : ""}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{it.deadline ? formatVnDate(it.deadline) : "—"}</td>
                      <td className={cn("px-2 py-1.5 whitespace-nowrap", it.lateBefore && "text-rose-600")}>
                        {it.oldEnd
                          ? `${it.oldStart ? `${formatVnDateTime(it.oldStart)} ` : ""}→ ${formatVnDateTime(it.oldEnd)}`
                          : "Chưa có lịch"}
                      </td>
                      <td className={cn("px-2 py-1.5 whitespace-nowrap font-semibold", it.lateAfter ? "text-rose-600" : "text-emerald-700")}>
                        {formatVnDateTime(it.newStart)} → {formatVnDateTime(it.newEnd)}
                        {it.lateAfter && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Huỷ
          </Button>
          <Button onClick={handleApply} disabled={saving || isLoading || items.length === 0} data-testid="resequence-apply">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            Lưu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
